// Route Pattern Matcher for Global Schema
// Matches route patterns with wildcards (* and **) and finds best matching schema

/**
 * Normalize route path by removing trailing slash and query/hash
 * @param {string} path - Route path
 * @returns {string} Normalized path
 */
function normalizePath(path) {
  if (!path) return '';
  // Remove query params and hash
  path = path.split('?')[0].split('#')[0];
  // Remove trailing slash (except for root)
  if (path.length > 1 && path.endsWith('/')) {
    path = path.slice(0, -1);
  }
  return path;
}

/**
 * Convert glob pattern to regex
 * @param {string} pattern - Pattern with * or ** wildcards
 * @returns {RegExp} Regular expression
 */
function patternToRegex(pattern) {
  if (!pattern) return null;
  
  // Escape special regex characters except * and /
  let regexStr = pattern
    .replace(/[.+?^${}()|[\]\\]/g, '\\$&');
  
  // Replace ** with .+ (matches anything including /)
  regexStr = regexStr.replace(/\*\*/g, '____DOUBLE____');
  // Replace * with [^/]+ (matches anything except /)
  regexStr = regexStr.replace(/\*/g, '[^/]+');
  // Restore **
  regexStr = regexStr.replace(/____DOUBLE____/g, '.+');
  
  return new RegExp(`^${regexStr}$`);
}

/**
 * Check if route matches pattern
 * @param {string} pattern - Pattern (e.g., '/admin/*', '/api/**')
 * @param {string} route - Route path to test
 * @returns {boolean} True if matches
 */
function matchRoutePattern(pattern, route) {
  // Handle null/undefined (but allow empty strings)
  if (pattern === null || pattern === undefined || route === null || route === undefined) {
    return false;
  }
  
  const normalizedPattern = normalizePath(pattern);
  const normalizedRoute = normalizePath(route);
  
  // Handle exact match (including empty strings)
  if (normalizedPattern === normalizedRoute) return true;
  
  // Handle wildcard patterns
  if (normalizedPattern.includes('*')) {
    const regex = patternToRegex(normalizedPattern);
    return regex ? regex.test(normalizedRoute) : false;
  }
  
  return false;
}

/**
 * Calculate specificity score for pattern (higher = more specific)
 * @param {string} pattern - Route pattern
 * @returns {number} Specificity score
 */
function getMatchScore(pattern) {
  if (!pattern) return 0;
  
  const normalized = normalizePath(pattern);
  if (!normalized) return 0;
  
  let score = 0;
  
  // Count path segments
  const segments = normalized.split('/').filter(s => s.length > 0);
  score += segments.length * 100;
  
  // Penalize wildcards
  let singleWildcards = 0;
  for (let i = 0; i < normalized.length; i += 1) {
    if (normalized[i] !== '*') continue;
    if (normalized[i - 1] === '*' || normalized[i + 1] === '*') continue;
    singleWildcards += 1;
  }
  const doubleWildcards = (normalized.match(/\*\*/g) || []).length;
  
  score -= singleWildcards * 20;
  score -= doubleWildcards * 50;
  
  // Exact paths (no wildcards) get bonus
  if (!normalized.includes('*')) {
    score += 1000;
  }
  
  return score;
}

/**
 * Find best matching schema for current route
 * @param {Object} globalSchema - Global schema object with default and routes
 * @param {string} currentRoute - Current route path
 * @returns {Object|null} Matching schema or null
 */
function findMatchingSchema(globalSchema, currentRoute) {
  // Backward compatibility: return null if no globalSchema
  if (!globalSchema || typeof globalSchema !== 'object') {
    return null;
  }
  
  // Return null if empty object
  if (Object.keys(globalSchema).length === 0) {
    return null;
  }
  
  const normalizedRoute = normalizePath(currentRoute);
  const routes = globalSchema.routes || [];
  
  let bestMatch = null;
  let bestScore = -1;
  
  // Find best matching route
  for (const routeConfig of routes) {
    if (!routeConfig || typeof routeConfig !== 'object') continue;
    if (!routeConfig.pattern) continue;
    
    if (matchRoutePattern(routeConfig.pattern, normalizedRoute)) {
      const score = getMatchScore(routeConfig.pattern, normalizedRoute);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = routeConfig.schema;
      }
    }
  }
  
  // Return matched schema or default
  if (bestMatch) {
    return bestMatch;
  }
  
  // Return default schema if it exists
  if (globalSchema.default && globalSchema.default.schema) {
    return globalSchema.default.schema;
  }
  
  // No match and no default
  return null;
}

module.exports = {
  matchRoutePattern,
  getMatchScore,
  findMatchingSchema,
  // Export internal functions for testing
  normalizePath,
  patternToRegex
};
